/** Editor для Специфікації номенклатури (Порція 4b).
 *
 * Особливість: довідник, не документ. Немає posted/unpost; лише save/delete.
 * One-active rule перевіряється backend → 400 з detail (показуємо у error div).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import {
  nomenclatureSpecifications,
  type NomenclatureSpecificationItemIn,
  type SpecificationPurpose,
} from '@/api/nomenclatureSpecifications';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';

const PERMISSION = 'production.specification.manage';

interface FormState {
  organization_id: string;
  product_nomenclature_id: string;
  base_quantity: string;
  name: string;
  description: string;
  purpose: SpecificationPurpose;
  is_active: boolean;
  items: NomenclatureSpecificationItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  product_nomenclature_id: '',
  base_quantity: '1',
  name: '',
  description: '',
  purpose: 'PRODUCTION',
  is_active: true,
  items: [],
};

const newRow = (n: number): NomenclatureSpecificationItemIn => ({
  line_number: n, raw_material_nomenclature_id: '', quantity: '0',
});

const sumPct = (items: NomenclatureSpecificationItemIn[]): number =>
  items.reduce((acc, it) => acc + (it.cost_percentage == null || it.cost_percentage === '' ? 0 : Number(it.cost_percentage)), 0);

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function NomenclatureSpecificationEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  // Префіл з закладки «Комплектація» картки: ?product=<nomId>&purpose=ASSEMBLY.
  const prefillProduct = searchParams.get('product');
  const prefillPurpose = searchParams.get('purpose') as SpecificationPurpose | null;

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.list({ limit: 500 }) });

  const docQuery = useQuery({
    queryKey: ['nomenclature-specification', id],
    queryFn: () => nomenclatureSpecifications.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({
        ...f,
        organization_id: availableOrgs[0]!.id,
        product_nomenclature_id: prefillProduct ?? f.product_nomenclature_id,
        purpose: prefillPurpose ?? f.purpose,
      }));
    }
  }, [isNew, availableOrgs, form.organization_id, prefillProduct, prefillPurpose]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        product_nomenclature_id: d.product_nomenclature_id,
        base_quantity: d.base_quantity,
        name: d.name,
        description: d.description ?? '',
        purpose: d.purpose ?? 'PRODUCTION',
        is_active: d.is_active,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          raw_material_nomenclature_id: it.raw_material_nomenclature_id,
          quantity: it.quantity,
          uom_id: it.uom_id,
          comment: it.comment,
          cost_percentage: it.cost_percentage,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const create = useMutation({
    mutationFn: () =>
      nomenclatureSpecifications.create({
        organization_id: form.organization_id,
        product_nomenclature_id: form.product_nomenclature_id,
        base_quantity: form.base_quantity,
        name: form.name,
        description: form.description || null,
        purpose: form.purpose,
        is_active: form.is_active,
        items: form.items,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['nomenclature-specifications'] });
      navigate(`/nomenclature-specifications/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      nomenclatureSpecifications.update(id!, {
        product_nomenclature_id: form.product_nomenclature_id,
        base_quantity: form.base_quantity,
        name: form.name,
        description: form.description || null,
        purpose: form.purpose,
        is_active: form.is_active,
        items: form.items,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nomenclature-specification', id] });
      qc.invalidateQueries({ queryKey: ['nomenclature-specifications'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: () => nomenclatureSpecifications.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nomenclature-specifications'] });
      navigate('/nomenclature-specifications');
    },
    onError: (e) => setError(extractError(e)),
  });

  function addItem() { setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] })); }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<NomenclatureSpecificationItemIn>) {
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  const isAssembly = form.purpose === 'ASSEMBLY';
  const total = sumPct(form.items);
  // Для ASSEMBLY сума долей має = 100 (з допуском на похибку відображення).
  const pctOk = !isAssembly || (form.items.length > 0 && Math.abs(total - 100) < 0.005);
  const saveDisabled = create.isPending || update.isPending || (isAssembly && !pctOk);

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова специфікація' : `Специфікація: ${form.name}`}
          {!isNew && !form.is_active && (
            <span style={{ marginLeft: '0.75rem', color: '#64748b', fontSize: '1rem' }}>
              (неактивна)
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/nomenclature-specifications')}>Назад</button>
          <RequirePermission code={PERMISSION}>
            <button className="primary" onClick={() => (isNew ? create.mutate() : update.mutate())}
                    disabled={saveDisabled}
                    title={isAssembly && !pctOk ? 'Сума долей собівартості має = 100%' : undefined}>Зберегти</button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button className="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Готова продукція</label>
          <select value={form.product_nomenclature_id}
                  onChange={(e) => setForm({ ...form, product_nomenclature_id: e.target.value })}>
            <option value="">—</option>
            {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Базова кількість</label>
          <input type="number" step="0.001" value={form.base_quantity}
                 onChange={(e) => setForm({ ...form, base_quantity: e.target.value })} />
        </div>
        <div className="field">
          <label>Призначення</label>
          <select value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value as SpecificationPurpose })}>
            <option value="PRODUCTION">Виробництво (рецептура)</option>
            <option value="ASSEMBLY">Набір (комплект)</option>
          </select>
        </div>
        <div className="field">
          <label>
            <input type="checkbox" checked={form.is_active}
                   onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {' '}Активна
          </label>
        </div>
        <div className="field">
          <label>Назва</label>
          <input value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="field">
          <label>Опис</label>
          <input value={form.description}
                 onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </div>

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>
        {isAssembly
          ? 'Склад набору (компоненти + долі собівартості)'
          : `Склад сировини на ${form.base_quantity} ${form.base_quantity === '1' ? 'одиницю' : 'одиниць'}`}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>{isAssembly ? 'Компонент' : 'Сировина'}</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            {isAssembly && <th style={{ padding: '0.5rem', width: 110 }}>Доля %</th>}
            <th style={{ padding: '0.5rem' }}>Примітка</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.raw_material_nomenclature_id}
                        onChange={(e) => setItemField(i, { raw_material_nomenclature_id: e.target.value })}>
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={it.quantity}
                       onChange={(e) => setItemField(i, { quantity: e.target.value })} />
              </td>
              {isAssembly && (
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" min="0" max="100"
                         value={it.cost_percentage ?? ''}
                         placeholder="%"
                         onChange={(e) => setItemField(i, { cost_percentage: e.target.value || null })} />
                </td>
              )}
              <td style={{ padding: '0.5rem' }}>
                <input value={it.comment ?? ''}
                       onChange={(e) => setItemField(i, { comment: e.target.value })} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <button onClick={() => removeItem(i)} title="Видалити">×</button>
              </td>
            </tr>
          ))}
        </tbody>
        {isAssembly && form.items.length > 0 && (
          <tfoot>
            <tr style={{ borderTop: '2px solid #e2e8f0' }}>
              <td colSpan={3} style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                Разом доля:
              </td>
              <td style={{ padding: '0.5rem', fontWeight: 600, color: pctOk ? 'inherit' : '#dc2626' }}
                  title={pctOk ? undefined : 'Сума долей має дорівнювати 100%'}>
                {total.toLocaleString('uk-UA', { maximumFractionDigits: 2 })}%
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
      <button onClick={addItem} style={{ marginTop: '0.5rem' }}>
        + Додати {isAssembly ? 'компонент' : 'сировину'}
      </button>

      {isAssembly && !pctOk && form.items.length > 0 && (
        <div style={{ marginTop: '0.5rem', color: '#dc2626', fontSize: '0.85rem' }}>
          Сума долей собівартості компонентів має дорівнювати 100% (зараз {total.toLocaleString('uk-UA', { maximumFractionDigits: 2 })}%).
        </div>
      )}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
